import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, MessageSquare, LogOut, Menu, X, ShieldCheck } from 'lucide-react';

const AdminLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const navLinks = [
        { name: 'Dashboard', path: '/admin', icon: <LayoutDashboard size={20} /> },
        { name: 'Enquiries', path: '/admin/enquiries', icon: <MessageSquare size={20} /> },
        { name: 'Users', path: '/admin/users', icon: <Users size={20} /> },
    ];

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Mobile Sidebar Toggle */}
            <button 
                className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-dark-green text-white rounded-lg"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
                {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Sidebar */}
            <aside className={`
                fixed inset-y-0 left-0 z-40 w-64 bg-dark-green text-white transition-transform transform 
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
                lg:translate-x-0 lg:static lg:inset-0
            `}>
                <div className="flex flex-col h-full">
                    <div className="p-6">
                        <Link to="/" className="flex flex-col">
                            <span className="text-xl font-bold tracking-tight">VSK CRM</span>
                            <span className="text-[10px] text-gold-primary font-semibold tracking-widest uppercase mt-1">Admin Panel</span>
                        </Link>
                    </div>

                    <nav className="flex-grow px-4 space-y-2">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`
                                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                                    ${location.pathname === link.path ? 'bg-gold-primary text-white' : 'hover:bg-white/10'}
                                `}
                                onClick={() => setIsSidebarOpen(false)}
                            >
                                {link.icon}
                                <span className="font-medium">{link.name}</span>
                            </Link>
                        ))}
                    </nav>

                    <div className="p-4 border-t border-white/10">
                        <div className="flex items-center space-x-3 px-4 py-3 mb-2">
                            <div className="w-8 h-8 bg-gold-primary rounded-full flex items-center justify-center font-bold">
                                {user?.name?.charAt(0)}
                            </div>
                            <div className="flex flex-col overflow-hidden">
                                <span className="text-sm font-bold truncate">{user?.name}</span>
                                <span className="text-[10px] text-gray-300 uppercase">{user?.role}</span>
                            </div>
                        </div>
                        <button 
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-red-500/20 text-red-300 transition-colors"
                        >
                            <LogOut size={20} />
                            <span className="font-medium">Logout</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-grow overflow-auto">
                <header className="bg-white shadow-sm px-8 py-4 hidden lg:block">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-dark-green">
                            {navLinks.find(l => l.path === location.pathname)?.name || 'Admin'}
                        </h2>
                        <div className="flex items-center space-x-2 text-dark-green font-semibold">
                            <ShieldCheck size={18} className="text-gold-primary" />
                            <span>VSK ASSOCIATES CRM</span>
                        </div>
                    </div>
                </header>
                <div className="p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
